import { useState, useEffect, useCallback, useRef } from 'react';
import { useJobSocketEvents } from '../../pages/dentist/DentistHomePage/hooks';
import { Job, ProcessedJob, DateJobsMap } from '../../types/dentist/calendar';
import { getAllJobs } from '../../api/dentist/calendarApi';
import { processJobs } from '../../utils/dentist/calendar/jobProcessing';

interface UseCalendarDataReturn {
  dateJobsMap: DateJobsMap;
  isLoading: boolean;
  error: string | null;
  refreshJobs: () => Promise<void>;
  getJobsForDate: (dateKey: string) => ProcessedJob[];
  rawJobs: Job[];
}

/**
 * Custom hook for managing calendar job data
 */
export const useCalendarData = (): UseCalendarDataReturn => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [dateJobsMap, setDateJobsMap] = useState<DateJobsMap>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Job ids this office has loaded, so socket events for other offices' jobs
  // do not trigger a full reload (see useJobSocketEvents).
  const ownJobIdsRef = useRef<Set<number> | null>(null);

  const fetchJobs = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const fetchedJobs = await getAllJobs();
      ownJobIdsRef.current = new Set(fetchedJobs.map((job) => Number(job.id)));
      setJobs(fetchedJobs);
      
      // Process jobs and group by date
      const processedMap = processJobs(fetchedJobs);
      
      // Optional: enrich with unread message counts from legacy Conversations client
      try {
        const conversations = (window as any)?.TempStars?.Conversations;
        if (conversations && typeof conversations.getUnreadMessages === 'function') {
          const unreadList = await conversations.getUnreadMessages();
          if (Array.isArray(unreadList)) {
            const jobIdToUnread: Record<number, number> = {};
            unreadList.forEach((item: any) => {
              const jobId = Number(item.jobId);
              const count = Number(item.unreadMessagesCount || 0);
              if (!isNaN(jobId) && count > 0) {
                jobIdToUnread[jobId] = count;
              }
            });
            // Annotate each job in the map
            Object.keys(processedMap).forEach((dateKey) => {
              processedMap[dateKey] = processedMap[dateKey].map((job) => {
                const count = jobIdToUnread[job.id];
                if (typeof count === 'number' && count > 0) {
                  (job as any).hasMessages = true;
                  (job as any).unreadMessageCount = count;
                }
                return job;
              });
            });
          }
        }
      } catch (e) {
        // ignore chat enrichment errors
      }
      
      // (logging removed)
      setDateJobsMap(processedMap);
    } catch (err) {
      console.error('Error fetching calendar jobs:', err);
      setError('Failed to load calendar data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Subscribe to chat unread updates and refresh jobs map on new messages/channels
  useEffect(() => {
    try {
      const conversations: any = (window as any)?.TempStars?.Conversations;
      if (!conversations) return;

      const handler = async () => {
        try {
          await fetchJobs();
        } catch (e) {
          // ignore
        }
      };

      if (typeof conversations.subscribeToNewMessages === 'function') {
        conversations.subscribeToNewMessages(handler);
      }
      if (typeof conversations.subscribeToNewChannels === 'function') {
        conversations.subscribeToNewChannels(handler);
      }

      return () => {
        try {
          if (typeof conversations.unsubscribeFromNewMessages === 'function') {
            conversations.unsubscribeFromNewMessages(handler);
          }
          if (typeof conversations.unsubscribeFromNewChannels === 'function') {
            conversations.unsubscribeFromNewChannels(handler);
          }
        } catch (e) {
          // ignore
        }
      };
    } catch (e) {
      // ignore
    }
  }, [fetchJobs]);

  const isOwnJob = useCallback((jobId: number) => {
    // Until the first load finishes, treat every job as possibly ours.
    return ownJobIdsRef.current === null || ownJobIdsRef.current.has(jobId);
  }, []);

  // Real-time refresh on socket events for this office's jobs
  useJobSocketEvents(fetchJobs, isOwnJob);

  const getJobsForDate = useCallback((dateKey: string): ProcessedJob[] => {
    const jobs = dateJobsMap[dateKey] || [];
    return jobs;
  }, [dateJobsMap]);

  return {
    dateJobsMap,
    isLoading,
    error,
    refreshJobs: fetchJobs,
    getJobsForDate,
    rawJobs: jobs
  };
}; 